import {AfterViewInit, Component, ElementRef, inject, OnDestroy, OnInit, viewChildren} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {NgForOf, NgIf, NgOptimizedImage} from '@angular/common';
import {AuthService} from '../../core/services/auth.service';
import {LoginModalService} from '../../shared/helpers/login-modal/login-modal.service';
import {createLogger} from '../../shared/helpers/logger/logger';
import Swal from 'sweetalert2';
import {ActivatedRoute} from '@angular/router';
import {RuntimeService} from '../../core/services/runtime.service';
import {RegisterService} from "../../core/services/register.service";
import {BsDatepickerConfig, BsDatepickerModule} from "ngx-bootstrap/datepicker";
import {PhoneInputComponent} from "../phone-input/phone-input.component";

const logger = createLogger('LoginModalComponent');

@Component({
    standalone: true,
    selector: 'app-login-modal',
    imports: [FormsModule, ReactiveFormsModule, NgIf, NgOptimizedImage, NgForOf, BsDatepickerModule, PhoneInputComponent],
    providers: [BsDatepickerConfig],
    templateUrl: './login-modal.component.html',
    styleUrls: ['./login-modal.component.scss']
})
export class LoginModalComponent implements OnInit, AfterViewInit, OnDestroy {
    loginForm: FormGroup;
    otpForm: FormGroup;
    otpSent = false;
    timer: number = 60;
    resendEnabled: boolean = false;
    errorMessage: string | null = null;
    readonly otpInputs = viewChildren<ElementRef>('otpInput');
    isSendingOtp: boolean = false;
    isVerifyingOtp: boolean = false;
    signUpForm: FormGroup;
    showSignUpForm = false;
    fid: string = '';
    private timerInterval: any;
    readonly #runtimeService = inject(RuntimeService);

    constructor(
        private fb: FormBuilder,
        private authService: AuthService,
        private loginModalService: LoginModalService,
        private activatedRoute: ActivatedRoute,
        private registerService: RegisterService
    ) {
        this.loginForm = this.fb.group({
            phone: new FormControl('', [Validators.required])
        });
        this.otpForm = this.fb.group({
            otpCode: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
        });
        this.signUpForm = this.fb.group({
            name: ['', [Validators.required, Validators.minLength(3)]],
            email: ['', [Validators.required, Validators.email]],
            username: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20), Validators.pattern(/^[a-zA-Z0-9_]+$/)]],
            dob: ['', Validators.required],
            gender: ['', Validators.required],
            tosConsent: [false, Validators.requiredTrue],
            whatsappConsent: [false]
        });
        this.authService.initRecaptchaVerifier('recaptcha-container').then(r => r);
    }

    async ngOnInit(): Promise<void> {
        await this.authService.initRecaptchaVerifier('recaptcha-container');
        this.activatedRoute.queryParams.subscribe(params => {
            const phone = params['phone'];
            if (phone) {
                this.loginForm.get('phone')?.setValue(phone);
                this.sendOtp();
            }
        });
        this.loginModalService.currentPhoneNumber$.subscribe(phone => {
            if (phone) {
                const phoneControl = this.loginForm.get('phone');
                phoneControl?.setValue(phone);
                phoneControl?.updateValueAndValidity();
                this.sendOtp();
            }
        });
    }

    async ngAfterViewInit(): Promise<void> {
        if (this.#runtimeService.isBrowser) {
            setTimeout(async () => {
                const recaptchaContainer = document.getElementById('recaptcha-container');
                if (recaptchaContainer && !this.authService.recaptchaVerifier) {
                    await this.authService.initRecaptchaVerifier('recaptcha-container');
                    if (this.loginForm.get('phone')?.value) {
                        await this.sendOtp();
                    }
                }
                if (this.loginModalService.showModal() && this.otpSent) {
                    setTimeout(() => {
                        const otpInputs = this.otpInputs();
                        if (otpInputs && otpInputs.length > 0) {
                            otpInputs.at(0)!.nativeElement.focus();
                        }
                    }, 0);
                }
            }, 100);
        }
    }

    async sendOtp() {
        if (!this.authService.recaptchaVerifier) {
            logger.error('RecaptchaVerifier not initialized');
            await Swal.fire('Error', 'reCAPTCHA not loaded. Please try again.', 'error');
            return;
        }
        if (this.loginForm.valid) {
            this.isSendingOtp = true;
            const phoneNumber = this.loginForm.get('phone')?.value;
            const fullPhoneNumber = phoneNumber?.internationalNumber;
            try {
                await this.authService.sendOtp(fullPhoneNumber);
                this.otpSent = true;
                this.startTimer();
                this.errorMessage = null;
                setTimeout(() => {
                    const otpInputs = this.otpInputs();
                    if (otpInputs && otpInputs.length > 0) {
                        otpInputs.at(0)!.nativeElement.focus();
                    }
                }, 0);
            } catch (error) {
                logger.error('Error sending OTP:', error);
                await Swal.fire('Error', 'Failed to send OTP. Please check your phone number.', 'error');
            } finally {
                this.isSendingOtp = false;
            }
        }
    }

    startTimer() {
        this.timer = 60;
        this.resendEnabled = false;
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
        }
        this.timerInterval = setInterval(() => {
            if (this.timer > 0) {
                this.timer--;
            } else {
                this.resendEnabled = true;
                clearInterval(this.timerInterval);
            }
        }, 1000);
    }

    async resendOtp() {
        if (this.resendEnabled) {
            this.resendEnabled = false;
            await this.sendOtp();
            this.startTimer();
        }
    }

    async verifyOtp() {
        if (this.otpForm.valid) {
            this.isVerifyingOtp = true;
            try {
                const result = await this.authService.confirmOtp(this.otpForm.value.otpCode);
                if (result && result.status === 'success') {
                    await this.loginModalService.notifyLoginSuccess(result.token);
                    this.close();
                } else if (result && result.status === 'newUser') {
                    const hasCallback = !!(this.loginModalService as any).userNotFoundCallback;
                    if (hasCallback) {
                        this.loginModalService.notifyUserNotFound(this.loginForm.get('phone')?.value);
                    } else {
                        this.showSignUpForm = true;
                        this.fid = result.fid;
                    }
                } else {
                    this.errorMessage = 'OTP verification failed. Please try again.';
                }
            } catch (error) {
                logger.error('OTP verification failed:', error);
                this.errorMessage = 'OTP verification failed. Please try again.';
            } finally {
                this.isVerifyingOtp = false;
            }
        }
    }

    async registerUser() {
        if (this.signUpForm.valid) {
            const formData = this.signUpForm.value;
            const phoneNumber = this.loginForm.get('phone')?.value.internationalNumber;

            // Format the DOB to 'DD-MM-YYYY'
            const dob = this.formatDate(formData.dob);

            try {
                const customer = await this.registerService.registerCustomer(
                    {
                        phone: phoneNumber,
                        fid: this.fid,
                        email: formData.email,
                        username: formData.username,
                        tos_consent: formData.tosConsent,
                        whatsapp_consent: formData.whatsappConsent,
                        referral: ''
                    },
                    {
                        name: formData.name,
                        dob: dob, // Use the formatted DOB
                        gender: formData.gender,
                        level: 'Novice',
                        brings_you: '',
                        art_to_you: '',
                        bio: ''
                    }
                );
                const token = await this.authService.getOAuthToken(this.fid);
                if (token) {
                    await this.loginModalService.notifyLoginSuccess(token);
                    this.close();
                } else {
                    this.errorMessage = 'Registration successful, but login failed. Please try logging in again.';
                }
            } catch (error) {
                logger.error('Registration failed:', error);
                this.errorMessage = 'Registration failed. Please try again.';
            }
        }
    }

    // Function to format the date to 'DD-MM-YYYY'
    formatDate(date: Date): string {
        if (!date) return '';
        const day = ('0' + date.getDate()).slice(-2);
        const month = ('0' + (date.getMonth() + 1)).slice(-2);
        const year = date.getFullYear();
        return `${year}-${month}-${day}`;
    }

    onOtpInput(event: any, index: number) {
        const value = event.target.value.replace(/[^0-9]/g, '');
        event.target.value = value;
        if (value.length === 1 && index < 5) {
            const nextInput = document.getElementById(`otp-${index + 1}`);
            if (nextInput) {
                nextInput.focus();
            }
        }
        this.updateOtpCode();
    }

    onOtpPaste(event: ClipboardEvent) {
        const pastedData = event.clipboardData?.getData('text') || '';
        if (/^\d{6}$/.test(pastedData)) {
            const otpInputs = document.querySelectorAll('.otp-input') as NodeListOf<HTMLInputElement>;
            pastedData.split('').forEach((digit, i) => {
                if (otpInputs[i]) {
                    otpInputs[i].value = digit;
                }
            });
            this.updateOtpCode();
            otpInputs[5].focus();
            event.preventDefault();
        }
    }

    onOtpKeydown(event: KeyboardEvent, index: number) {
        const input = event.target as HTMLInputElement;
        if (event.key === 'Backspace' && index > 0 && !input.value) {
            const prevInput = document.getElementById(`otp-${index - 1}`);
            if (prevInput) {
                prevInput.focus();
            }
        }
    }

    updateOtpCode() {
        const otpInputs = document.querySelectorAll('.otp-input') as NodeListOf<HTMLInputElement>;
        let otp = '';
        otpInputs.forEach((input) => {
            otp += input.value;
        });
        this.otpForm.get('otpCode')?.setValue(otp);
        this.otpForm.get('otpCode')?.updateValueAndValidity();
    }

    close() {
        this.loginModalService.hide();
        try {
            this.otpSent = false;
            this.showSignUpForm = false;
            this.loginForm.reset();
            this.otpForm.reset();
            this.signUpForm.reset();
            this.errorMessage = null;
            this.fid = '';
            if (this.timerInterval) {
                clearInterval(this.timerInterval);
            }
            this.authService.clearRecaptchaVerifier();
        } catch (error) {
            logger.error('Error during cleanup:', error);
        }
    }

    ngOnDestroy(): void {
        this.authService.clearRecaptchaVerifier();
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
        }
    }
}
